import { useEffect, useMemo, useState } from 'react'

export interface PdfPageThumbnail {
  pageNumber: number
  dataUrl: string
  width: number
  height: number
  rotation: number
}

export type PdfPreviewStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface PdfPreviewState {
  status: PdfPreviewStatus
  totalPages: number | null
  thumbnails: PdfPageThumbnail[]
  error: string | null
}

interface UsePdfPageThumbnailsOptions {
  file: File | null
  pageNumbers?: readonly number[]
  maxPages?: number
  targetWidth?: number
  onDocumentLoad?: (totalPages: number) => void
}

interface PdfJsModule {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfJsDocument>; destroy: () => Promise<void> }
}

interface PdfJsDocument {
  numPages: number
  getPage: (n: number) => Promise<PdfJsPage>
}

interface PdfJsPage {
  rotate: number
  getViewport: (opts: { scale: number }) => PdfJsViewport
  render: (opts: { canvasContext: CanvasRenderingContext2D; viewport: PdfJsViewport }) => {
    promise: Promise<void>
    cancel: () => void
  }
  cleanup: () => void
}

interface PdfJsViewport {
  width: number
  height: number
}

interface PdfLoadingTask {
  promise: Promise<PdfJsDocument>
  destroy: () => Promise<void>
}

const DEFAULT_MAX_PAGES = 12
const DEFAULT_TARGET_WIDTH = 180

export function usePdfPageThumbnails({
  file,
  pageNumbers,
  maxPages = DEFAULT_MAX_PAGES,
  targetWidth = DEFAULT_TARGET_WIDTH,
  onDocumentLoad,
}: UsePdfPageThumbnailsOptions): PdfPreviewState {
  const requestedPagesKey = useMemo(
    () => normalizeRequestedPages(pageNumbers, maxPages).join(','),
    [maxPages, pageNumbers],
  )
  const [state, setState] = useState<PdfPreviewState>({
    status: file ? 'loading' : 'idle',
    totalPages: null,
    thumbnails: [],
    error: null,
  })

  useEffect(() => {
    if (!file) {
      setState({ status: 'idle', totalPages: null, thumbnails: [], error: null })
      return undefined
    }

    const controller = new AbortController()
    const requestedPages = normalizeRequestedPages(pageNumbers, maxPages)

    setState({ status: 'loading', totalPages: null, thumbnails: [], error: null })

    renderThumbnails({
      file,
      requestedPages,
      maxPages,
      targetWidth,
      signal: controller.signal,
      onDocumentLoad: (totalPages) => {
        onDocumentLoad?.(totalPages)
        setState((current) => ({ ...current, totalPages }))
      },
      onThumbnail: (thumbnail) => {
        setState((current) => ({
          ...current,
          status: 'loading',
          thumbnails: [...current.thumbnails, thumbnail],
        }))
      },
    })
      .then((result) => {
        if (controller.signal.aborted) return
        setState({
          status: 'ready',
          totalPages: result.totalPages,
          thumbnails: result.thumbnails,
          error: null,
        })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setState((current) => ({
          status: 'error',
          totalPages: current.totalPages,
          thumbnails: current.thumbnails,
          error: error instanceof Error ? error.message : 'preview_failed',
        }))
      })

    return () => controller.abort()
  }, [file, maxPages, requestedPagesKey, targetWidth])

  return state
}

interface RenderThumbnailsOptions {
  file: File
  requestedPages: number[]
  maxPages: number
  targetWidth: number
  signal: AbortSignal
  onDocumentLoad: (totalPages: number) => void
  onThumbnail: (thumbnail: PdfPageThumbnail) => void
}

async function renderThumbnails({
  file,
  requestedPages,
  maxPages,
  targetWidth,
  signal,
  onDocumentLoad,
  onThumbnail,
}: RenderThumbnailsOptions): Promise<{ totalPages: number; thumbnails: PdfPageThumbnail[] }> {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsModule
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const loadingTask = await createPdfLoadingTask(file, signal, pdfjs.getDocument)
  return withPdfLoadingTask(loadingTask, signal, async (doc) => {
    const totalPages = doc.numPages
    onDocumentLoad(totalPages)

    const pagesToRender = requestedPages.length > 0
      ? requestedPages.filter((page) => page >= 1 && page <= totalPages).slice(0, maxPages)
      : Array.from({ length: Math.min(totalPages, maxPages) }, (_, index) => index + 1)

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas_unavailable')

    const thumbnails: PdfPageThumbnail[] = []

    for (const pageNumber of pagesToRender) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

      const page = await doc.getPage(pageNumber)
      try {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
        const baseViewport = page.getViewport({ scale: 1 })
        const scale = Math.min(targetWidth / baseViewport.width, 1)
        const viewport = page.getViewport({ scale })

        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)

        const task = page.render({ canvasContext: ctx, viewport })
        const onAbort = () => task.cancel()
        signal.addEventListener('abort', onAbort, { once: true })
        try {
          await task.promise
        } finally {
          signal.removeEventListener('abort', onAbort)
        }

        if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
        const thumbnail = {
          pageNumber,
          dataUrl: canvas.toDataURL('image/jpeg', 0.72),
          width: canvas.width,
          height: canvas.height,
          rotation: ((page.rotate % 360) + 360) % 360,
        }
        thumbnails.push(thumbnail)
        onThumbnail(thumbnail)
      } finally {
        page.cleanup()
      }
    }

    canvas.width = 0
    canvas.height = 0

    return { totalPages, thumbnails }
  })
}

export async function createPdfLoadingTask(
  file: Pick<File, 'arrayBuffer'>,
  signal: AbortSignal,
  getDocument: (src: { data: ArrayBuffer }) => PdfLoadingTask,
): Promise<PdfLoadingTask> {
  const bytes = await file.arrayBuffer()
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
  return getDocument({ data: bytes })
}

export async function withPdfLoadingTask<T>(
  loadingTask: PdfLoadingTask,
  signal: AbortSignal,
  operation: (doc: PdfJsDocument) => Promise<T>,
): Promise<T> {
  let destroyPromise: Promise<void> | undefined
  const destroyOnce = () => {
    destroyPromise ??= Promise.resolve()
      .then(() => loadingTask.destroy())
      .catch(() => {})
    return destroyPromise
  }
  let rejectAbort!: (reason: DOMException) => void
  const aborted = new Promise<never>((_, reject) => { rejectAbort = reject })
  const onLoadAbort = () => {
    void destroyOnce()
    rejectAbort(new DOMException('Aborted', 'AbortError'))
  }
  signal.addEventListener('abort', onLoadAbort, { once: true })

  try {
    if (signal.aborted) onLoadAbort()
    const doc = await Promise.race([loadingTask.promise, aborted])
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
    return await operation(doc)
  } finally {
    signal.removeEventListener('abort', onLoadAbort)
    await destroyOnce()
  }
}

function normalizeRequestedPages(
  pageNumbers: readonly number[] | undefined,
  maxPages: number,
): number[] {
  if (!pageNumbers) return []
  const unique = Array.from(new Set(pageNumbers.filter((page) => Number.isInteger(page) && page > 0)))
  return unique.slice(0, maxPages)
}
